import { FormField } from 'ui/formField';
import { BasicSelectField } from 'lib/iterable-hook-form/FormField';
import { HelpToggletip, TipPlacement } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { useWatch } from 'react-hook-form';
import { useCampaignContext } from 'modules/campaigns/editor/context';
import { ALIGNMENT } from 'ui/selects';
import { getIsSTOEnabled } from 'modules/campaigns/editor/utils';
import { RecurrenceScheduleType } from 'modules/campaigns/lib/types';
import { Moment } from 'moment';
import { useMemo } from 'react';
import { match } from 'ts-pattern';
import { getIsRecurringScheduleType } from 'modules/campaigns/lib/utils';
import { CampaignState } from 'modules/domains/campaign';
import { CAMPAIGN_RECURRENCE_OPTIONS } from '../ScheduleCampaignModal.constants';
import { RowWrapper } from '../ScheduleCampaignModal.styled';

const getRecurrenceOptions = (startDate: Moment, campaignState: CampaignState) => {
  const day = startDate?.format('dddd');

  return CAMPAIGN_RECURRENCE_OPTIONS.map((option) => {
    const getOptionWithDay = () => ({ ...option, label: `${option.label} on ${day}s` });

    return match(option.id)
      .with(RecurrenceScheduleType.Weekly, getOptionWithDay)
      .with(RecurrenceScheduleType.BiWeekly, getOptionWithDay)
      .otherwise(() => option);
  }).filter((option) => {
    /**
     * Recurring campaigns can not be rescheduled to be non-recurring.
     */
    if (
      campaignState === CampaignState.Recurring &&
      option.id === RecurrenceScheduleType.DoNotRepeat
    ) {
      return false;
    }

    return true;
  });
};

export const Recurrence = () => {
  const {
    campaign: { campaignState, dataScienceOptimizations },
  } = useCampaignContext();

  const [recurrence, startDate] = useWatch({ name: ['recurrence', 'startDate'] });

  const options = useMemo(
    () => getRecurrenceOptions(startDate, campaignState),
    [startDate, campaignState],
  );

  const isRecurringScheduleType = getIsRecurringScheduleType(recurrence);
  const isSTOEnabled = getIsSTOEnabled(dataScienceOptimizations);

  return (
    <FormField labelText="Repeat">
      <RowWrapper>
        <BasicSelectField
          name="recurrence"
          id="recurrence-options"
          title="recurrence-options"
          options={options}
          verticalAlignment={
            !isSTOEnabled && !isRecurringScheduleType ? ALIGNMENT.UP : ALIGNMENT.DOWN
          }
        />
        {isRecurringScheduleType && (
          <HelpToggletip
            placement={TipPlacement.Right}
            tooltipContent="For every recurrence, a new campaign is created by cloning the settings in this campaign and the template."
            iconProps={{ color: colors.secondaryText }}
          />
        )}
      </RowWrapper>
    </FormField>
  );
};
